/**
 * i18n (web). Same exports as the mobile i18n.config.js so vendored code
 * (`getCurrentLanguage`, `LANGUAGE_NAMES`, …) resolves unchanged. Resources
 * come straight from the mobile locale files via the `@locales` alias.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import moment from 'moment'
import 'moment/dist/locale/es'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/de'
import 'moment/dist/locale/pt'

import en from '@locales/en.json'
import es from '@locales/es.json'
import fr from '@locales/fr.json'
import de from '@locales/de.json'
import pt from '@locales/pt.json'

export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'pt'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
}

export const LANGUAGE_FLAGS: Record<Language, string> = {
  en: '🇺🇸',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  pt: '🇧🇷',
}

const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  de: { translation: de },
  pt: { translation: pt },
}

const LANGUAGE_STORAGE_KEY = 'app_language'

function applyLang(lang: string) {
  const base = (lang || 'en').split('-')[0]
  document.documentElement.lang = base
  moment.locale(base)
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    // A few web keys collide with nested namespaces in the shared files; fall
    // back to the English default passed as the 2nd arg instead of erroring.
    returnedObjectHandler: (key: string, _value: unknown, options?: { defaultValue?: unknown }) => {
      const dv = options?.defaultValue
      return typeof dv === 'string' ? dv : key.split('.').pop() || key
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  })
  .then(() => applyLang(i18n.resolvedLanguage || i18n.language))

i18n.on('languageChanged', applyLang)

export const loadSavedLanguage = async (): Promise<void> => {
  /* the browser detector already restored it */
}

export const changeLanguage = async (lang: string): Promise<void> => {
  await i18n.changeLanguage(lang)
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
  } catch {
    /* ignore */
  }
}

export const getCurrentLanguage = (): Language => {
  const base = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? (base as Language) : 'en'
}

export default i18n
